import { createFileRoute, useParams } from '@tanstack/react-router'
import { memo, useCallback } from 'react'
import { useRouteContext } from '@tanstack/react-router'
import { env } from '@/env'
import { useDevModeStore } from '@/stores/useDevModeStore'
import { useIdTokenAndTenantId } from '@/hooks/useIdTokenAndTenantId'
import { useDioxusWithCallbacks } from '@/lib/wasm'
import { LoadingDocumentEditContent } from '@/components/loading-ui/LoadingDocument'

// The diagnostics tab is rendered by the Rust/Dioxus
// `mount_diagnostics_tab` WASM component. The React side keeps the
// route registration (TanStack needs the `createFileRoute` declaration
// in the file tree), gates on `useDevModeStore`, and otherwise just
// forwards `tenantId` / `projectId` / `sopId` to the WASM component.
//
// `shiki` and `react-markdown` / `remark-gfm` were dropped from the
// React bundle as part of this port — the equivalent rendering lives
// in the WASM bundle via `syntect` + `pulldown-cmark`.
export const Route = createFileRoute(
  '/_authenticated/org/$orgId/projects/$projectId/sops/$sopId/diagnostics',
)({
  component: memo(RouteComponent),
  pendingComponent: () => <LoadingDocumentEditContent />,
})

function RouteComponent() {
  const { projectId, sopId } = useParams({
    from: '/_authenticated/org/$orgId/projects/$projectId/sops/$sopId/diagnostics',
  })
  const devMode = useDevModeStore((state) => state.devMode)

  // Defense in depth: the tab is hidden in the header when dev-mode is
  // off, but a user with a stale URL could still land here directly.
  // Show a friendly empty-state instead of mounting the WASM component
  // (which would otherwise fire the diagnostics fetch).
  if (!devMode) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-12">
        <p className="text-copy-secondary text-center">
          Diagnostics is a developer-only view. Enable dev mode in advanced
          settings to use it.
        </p>
      </div>
    )
  }

  return <DiagnosticsTab projectId={projectId} sopId={sopId} />
}

function DiagnosticsTab({
  projectId,
  sopId,
}: {
  projectId: string
  sopId: string
}) {
  const { authState } = useRouteContext({ from: '/_authenticated' })
  const { TENANT_ID } = useIdTokenAndTenantId()

  const getFirebaseToken = useCallback(
    () => (authState ? authState.getIdToken() : Promise.resolve('')),
    [authState],
  )

  const ref = useDioxusWithCallbacks({
    mountFn: 'mount_diagnostics_tab',
    props: {
      baseUrl: env.VITE_API_BASE_URL,
      tenantId: TENANT_ID ?? '',
      projectId,
      sopId,
    },
    callbacks: [getFirebaseToken],
  })

  if (!authState || !TENANT_ID) return null
  return <div ref={ref} />
}
